import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/components/Layout';
import { dashboard } from '@/lib/api';
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '@/lib/utils';
import { TrendingUp, ShoppingBag, Clock, Truck, DollarSign, Package } from 'lucide-react';
import { toast } from 'sonner';

export function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboard.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Carregando...</div>
        </div>
      </MainLayout>
    );
  }

  const kpiCards = [
    { icon: DollarSign, label: 'Receita Hoje', value: formatCurrency(stats?.today_revenue || 0), color: 'bg-emerald-500' },
    { icon: ShoppingBag, label: 'Pedidos Hoje', value: stats?.today_orders || 0, color: 'bg-blue-500' },
    { icon: Clock, label: 'Pedidos Pendentes', value: stats?.pending_orders || 0, color: 'bg-orange-500' },
    { icon: Truck, label: 'Entregas Ativas', value: stats?.active_deliveries || 0, color: 'bg-purple-500' },
    { icon: TrendingUp, label: 'Receita Semana', value: formatCurrency(stats?.week_revenue || 0), color: 'bg-rose-500' },
    { icon: Package, label: 'Receita Mês', value: formatCurrency(stats?.month_revenue || 0), color: 'bg-indigo-500' },
  ];

  return (
    <MainLayout>
      <div className="space-y-8 page-enter" data-testid="dashboard-page">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            Dashboard
          </h1>
          <p className="text-slate-600">Visão geral do seu negócio</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {kpiCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={index}
                data-testid={`kpi-card-${index}`}
                className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`${card.color} w-12 h-12 rounded-lg flex items-center justify-center`}>
                    <Icon className="text-white" size={24} />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">{card.label}</p>
                  <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                    {card.value}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              Pedidos Recentes
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">ID</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Cliente</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Origem</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Valor</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Data</th>
                </tr>
              </thead>
              <tbody>
                {stats?.recent_orders && stats.recent_orders.length > 0 ? (
                  stats.recent_orders.map((order) => (
                    <tr
                      key={order.id}
                      data-testid={`order-row-${order.id}`}
                      className="bg-white hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                    >
                      <td className="px-6 py-4 text-sm text-slate-600">{order.id.substring(0, 8)}...</td>
                      <td className="px-6 py-4 text-sm text-slate-900 font-medium">{order.customer_name || 'Anônimo'}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{order.origin}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900">{formatCurrency(order.total)}</td>
                      <td className="px-6 py-4 text-sm text-slate-500">{formatDate(order.created_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                      Nenhum pedido encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
